import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@lib/queryKeys';
import {
  fetchDevices,
  toggleDeviceEnabled,
  deleteDevice,
} from '../services/deviceService';

/**
 * Hook to fetch registered devices for an outlet
 */
export function useDevices(outletId?: string | null) {
  return useQuery({
    queryKey: queryKeys.devices.list(outletId),
    queryFn: async () => {
      const res = await fetchDevices(outletId);
      if (!res.success) {
        throw new Error(res.error || 'Failed to fetch devices');
      }
      return res.data;
    },
  });
}

/**
 * Hook to manage device updates/deletes using TanStack mutations
 */
export function useDeviceMutations() {
  const queryClient = useQueryClient();

  const toggleMutation = useMutation({
    mutationFn: async ({
      deviceId,
      isEnabled,
    }: {
      deviceId: string;
      isEnabled: boolean;
    }) => {
      const res = await toggleDeviceEnabled(deviceId, isEnabled);
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to toggle device');
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.devices.all });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (deviceId: string) => {
      const res = await deleteDevice(deviceId);
      if (!res.success) {
        throw new Error(res.error || 'Failed to delete device');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.devices.all });
    },
  });

  return { toggleMutation, deleteMutation };
}
